import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A paginated grid. Page boundaries come from server metadata, never from the
 * number of rendered cards: editorial review guards can leave any page empty.
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid]
 * @property {HTMLSpanElement} [viewMorePrevious]
 * @property {HTMLSpanElement} [viewMoreNext]
 * @property {HTMLElement[]} [cards]
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  pages = new Map();
  infinityScrollObserver;
  #aspectRatioHelper;
  #firstPage = 1;
  #lastPage = 1;
  #generation = 0;
  #filtering = false;
  #rendering = new Set();
  #pendingFetches = new Map();
  #failedPages = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#generation += 1;
    this.#filtering = false;
    this.#resetPagination();
    this.#observeViewMore();
    this.#prefetchAdjacent();
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#generation += 1;
    this.infinityScrollObserver?.disconnect();
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #resetPagination() {
    this.pages.clear();
    this.#pendingFetches.clear();
    this.#failedPages.clear();
    this.#rendering.clear();
    const page = Number(this.refs.grid?.dataset.currentPage || new URL(window.location.href).searchParams.get('page') || 1);
    this.#firstPage = this.#lastPage = Number.isInteger(page) && page > 0 ? page : 1;
    const templateCard = this.querySelector('[ref="cardGallery"]');
    this.#aspectRatioHelper = templateCard ? new PaginatedListAspectRatioHelper({ templateCard }) : undefined;
    this.querySelectorAll('[data-pagination-status]').forEach((element) => element.remove());
  }

  #observeViewMore() {
    this.infinityScrollObserver?.disconnect();
    const { viewMorePrevious, viewMoreNext } = this.refs;
    if (!viewMorePrevious && !viewMoreNext) return;
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(async (entries) => {
        if (viewTransition.current) await viewTransition.current;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (entry.target === this.refs.viewMorePrevious) void this.#renderPage('previous');
          else if (entry.target === this.refs.viewMoreNext) void this.#renderPage('next');
        }
      }, { rootMargin: '100px' });
    }
    if (viewMorePrevious) this.infinityScrollObserver.observe(viewMorePrevious);
    if (viewMoreNext) this.infinityScrollObserver.observe(viewMoreNext);
  }

  #shouldUsePage(pageInfo) {
    const lastPage = Number(this.refs.grid?.dataset.lastPage);
    return !!pageInfo && Number.isInteger(lastPage) && pageInfo.page >= 1 && pageInfo.page <= lastPage;
  }

  #getPage(type) {
    const page = type === 'previous' ? this.#firstPage - 1 : this.#lastPage + 1;
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('page', page.toString());
    url.hash = '';
    return { page, url };
  }

  #prefetchAdjacent() {
    if (this.#filtering || !this.isConnected) return;
    for (const type of ['next', 'previous']) {
      const marker = type === 'next' ? this.refs.viewMoreNext : this.refs.viewMorePrevious;
      const pageInfo = this.#getPage(type);
      if (marker && this.#shouldUsePage(pageInfo) && this.#failedPages.get(type) !== pageInfo.page) {
        // A speculative failure is retried once when the buyer reaches the edge.
        void this.#fetchSpecificPage(pageInfo).catch(() => {});
      }
    }
  }

  async #fetchSpecificPage(pageInfo) {
    if (this.pages.has(pageInfo.page)) return this.pages.get(pageInfo.page);
    if (this.#pendingFetches.has(pageInfo.page)) return this.#pendingFetches.get(pageInfo.page);
    const generation = this.#generation;
    // SectionRenderer adds section_id in place; never let its transport URL leak
    // into browser history or the buyer's normal-navigation retry link.
    const pending = sectionRenderer.getSectionHTML(this.sectionId, true, new URL(pageInfo.url)).then((html) => {
      if (generation === this.#generation) this.pages.set(pageInfo.page, html);
      return html;
    });
    this.#pendingFetches.set(pageInfo.page, pending);
    try {
      return await pending;
    } finally {
      if (this.#pendingFetches.get(pageInfo.page) === pending) this.#pendingFetches.delete(pageInfo.page);
    }
  }

  async #renderPage(type) {
    const { grid } = this.refs;
    if (!grid || this.#filtering || this.#rendering.has(type) || !this.isConnected) return;
    const generation = this.#generation;
    this.#rendering.add(type);
    let lastUrl;
    try {
      // The server's actual final page is also the hard bound on empty-page work.
      const maxSteps = Number(grid.dataset.lastPage);
      for (let step = 0; step < maxSteps; step += 1) {
        const pageInfo = this.#getPage(type);
        if (!this.#shouldUsePage(pageInfo) || this.#failedPages.get(type) === pageInfo.page) break;
        let parsedGrid;
        try {
          const html = await this.#fetchSpecificPage(pageInfo);
          if (generation !== this.#generation || !this.isConnected || this.refs.grid !== grid) return;
          parsedGrid = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="grid"]');
          if (!parsedGrid || Number(parsedGrid.dataset.currentPage) !== pageInfo.page) {
            throw new Error('The requested product page was not returned.');
          }
        } catch (error) {
          if (generation !== this.#generation || !this.isConnected) return;
          this.#failedPages.set(type, pageInfo.page);
          this.pages.delete(pageInfo.page);
          this.#showPageFailure(type, pageInfo.url);
          break;
        }

        const cards = [...parsedGrid.querySelectorAll(':scope > [ref="cards[]"]')];
        const firstElement = grid.firstElementChild;
        const oldTop = firstElement ? firstElement.getBoundingClientRect().top + getScrollTop() : 0;
        if (type === 'previous') {
          grid.prepend(...cards);
          this.#firstPage = pageInfo.page;
        } else {
          grid.append(...cards);
          this.#lastPage = pageInfo.page;
        }
        // Empty pages still advance. They must not be retried from the last card.
        lastUrl = pageInfo.url;
        this.#aspectRatioHelper?.processNewElements();
        if (type === 'previous' && firstElement && cards.length) {
          const newTop = firstElement.getBoundingClientRect().top + getScrollTop();
          scrollTo({ top: getScrollTop() + newTop - oldTop, behavior: 'instant' });
        }
        await yieldToMainThread();
        if (generation !== this.#generation || !this.isConnected || this.refs.grid !== grid) return;
        if (cards.length) break;
      }
      if (lastUrl && generation === this.#generation) history.pushState('', '', lastUrl.toString());
      if (!grid.querySelector('[ref="cards[]"]') && this.#firstPage === 1 && this.#lastPage >= Number(grid.dataset.lastPage)) {
        this.#showEmptyState();
      }
    } finally {
      if (generation === this.#generation) {
        this.#rendering.delete(type);
        requestIdleCallback(() => this.#prefetchAdjacent());
      }
    }
  }

  #showPageFailure(type, url) {
    if (this.querySelector(`[data-pagination-status="${type}"]`)) return;
    const message = document.createElement('p');
    message.dataset.paginationStatus = type;
    message.setAttribute('role', 'status');
    message.textContent = 'More products could not be loaded. ';
    const link = document.createElement('a');
    link.href = url.toString();
    link.textContent = 'Continue browsing';
    message.append(link);
    const marker = type === 'next' ? this.refs.viewMoreNext : this.refs.viewMorePrevious;
    if (marker) marker.after(message);
    else this.append(message);
  }

  #showEmptyState() {
    if (this.querySelector('[data-pagination-status="empty"]')) return;
    const message = document.createElement('p');
    message.dataset.paginationStatus = 'empty';
    message.setAttribute('role', 'status');
    message.textContent = 'No products to display in this view.';
    this.refs.grid?.after(message);
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  #handleFilterUpdate = async (event) => {
    const eventSection = event.target?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    const generation = ++this.#generation;
    this.#filtering = true;
    this.infinityScrollObserver?.disconnect();
    try {
      // Shopify's standard filter events carry the section-render completion promise.
      // Await it even when the new grid has the same page count as the old grid.
      await event.promise;
      if (generation !== this.#generation || !this.isConnected) return;
      this.#resetPagination();
      this.#filtering = false;
      this.#observeViewMore();
      this.#prefetchAdjacent();
    } catch (error) {
      if (generation !== this.#generation || !this.isConnected) return;
      // Keep the stale grid paused; the normal link retries the selected URL.
      this.#showPageFailure('next', new URL(window.location.href));
    }
  };
}
